/** @jsx isolateComponent */
import { useRef, useState } from 'react';
import { css } from '@emotion/core';

import { isolateComponent } from '@zapier/style-encapsulation';
import {
  Animation,
  Colors,
  Heading,
  Icon,
  Link,
  Shadows,
  Typography,
} from '@zapier/design-system';

import { SizeType } from '@zapier/product-get-help/src/types';

import { rebrandFocusCss } from '@zapier/product-get-help/src/styles';

type ConversationLinkProps = {
  href: string;
  title: string;
  snippet?: string;
  size: SizeType;
};

const titleCss = (isActive: boolean) => css`
  ${Typography.paragraph1Bold}
  color: ${Colors[isActive ? 'blueJeans' : 'neutral800']};
`;

const contentCss = (size: SizeType) => css`
  display: block;
  border: 1px solid ${Colors.neutral400};
  border-radius: 10px;
  background-color: ${Colors.neutral100};
  ${Typography.smallPrint1}
  color: ${Colors.neutral700};
  padding: ${size === 'small' ? '10px' : '20px 28px'};
  display: grid;
  grid-row-gap: 5px;
  textDecoration: none;
  transition-property: shadow;
  transition-timing-function: ${Animation.transitionTimingFunction};
  transition-duration: ${Animation.transitionDuration};
  &:hover, &:focus {
    box-shadow: ${Shadows.elevation10};
  }
  ${rebrandFocusCss}
`;

const rebrandSnippetCss = css`
  ${Typography.smallPrint1}
  color: ${Colors.neutral700};
`;

const readMoreCss = css`
  font-weight: 700;
  display: grid;
  align-items: center;
  grid-template-columns: max-content auto;
  column-gap: 5px;
`;

const removeTextUnderlineCss = {
  'text-decoration': 'none',
};

/** Renders the content for the community-step in the form. */
const ConversationLink = ({
  href,
  title,
  size,
  snippet,
}: ConversationLinkProps) => {
  const [isActive, setIsActive] = useState(false);

  const onBlur = useRef(() => setIsActive(false)).current;
  const onFocus = useRef(() => setIsActive(true)).current;
  const onMouseEnter = useRef(() => setIsActive(true)).current;
  const onMouseLeave = useRef(() => setIsActive(false)).current;

  return (
    <Link
      css={contentCss(size)}
      href={href}
      onBlur={onBlur}
      onFocus={onFocus}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={removeTextUnderlineCss}
      target="_blank"
    >
      <Heading css={titleCss(isActive)}>{title}</Heading>
      {snippet && <p css={rebrandSnippetCss}>{snippet}</p>}
      <span css={readMoreCss}>
        Read more <Icon name="arrowOffsite" size={18} />
      </span>
    </Link>
  );
};

export default ConversationLink;
